// DE ONDE O CLIENTE CONHECEU A LOJA — bloco do Enviar ao Caixa (migration 0322, 16/10/2026).
//
// Pedido dele: *"acrescentariamos um bloco perguntando como ele descobriu nossa loja ... para
// direcionarmos melhor nossas estrategias de marketing"*. Decisoes dele na mesma conversa:
//   - fica ABAIXO do bloco "Quem esta comprando?";
//   - UMA resposta por CLIENTE: identificou, ja aparece de onde veio, e ninguem preenche de novo;
//   - NAO obrigatoria — nada aqui trava o Enviar;
//   - as opcoes vem do cadastro (Arquivos > Cadastros Auxiliares > Origem do Cliente).
//
// ⛔ Nada nasce marcado: a marca que aparece e' a que JA esta gravada (no cliente ou nesta venda),
// nunca um default — numa tela que grava, caixa pre-marcada e' decidir pela vendedora.
// ⛔ Grava no CLIQUE, sem segundo botao ([DA-707]): campo que so' vale com outro botao e' o que se
// esquece.
//
// ⛔ **RESPONDIDA UMA VEZ, TRAVA** (decisao dele, 16/10/2026: *"a partir da primeira o sistema nao
// deve permitir uma segunda marcacao"*). A vendedora so' troca enquanto o modal em que ELA marcou
// esta aberto — a janela do clique errado, o mesmo desenho das etiquetas ([DA-722]) —; fechou,
// trava. *"O Admin pode marcar livremente"*. O servidor recusa igual (409).
//
// ✅ **Modo CONTATO (`contatoId`)** — o "Perfil" do menu da conversa (pedido dele, 26/10/2026). Le e
// grava no CONTATO do WhatsApp (migration 0345: *"Marcar no cadastro do contato"*), nunca na ficha —
// a ficha pode ser da empresa em nome de quem ele compra, ou nao existir.
// ⛔ **O contato e' a FONTE UNICA da origem** (0346, *"a origem primaria deve ser uma so'"*): no modo
// venda o servidor tambem grava no contato da ficha, e so' sem contato a origem fica no pedido.
// A regra do Perfil e' OUTRA, tambem dele: *"o sistema deve permitir modificar so' os campos em
// branco"* — vale pra admin tambem. Folga unica: os 15 minutos depois da primeira resposta, que o
// SERVIDOR mede (`pode_alterar`) — a janela do clique errado, igual a da etiqueta.
import { useEffect, useState } from 'react';
import { Check, Loader2, Lock } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDataSP } from '@fiodecobre/shared';

interface Origem { id: number; nome: string; ativo: boolean }

interface Props {
  orcamentoId: number | null;
  /** O cliente da TELA (o autosave leva ao banco segundos depois). 1 = Consumidor, sem ficha. */
  clienteId: number;
  clienteNome?: string;
  /**
   * Marcou NESTE modal — a janela em que a vendedora ainda troca. ⚠️ Mora no MODAL, nao aqui:
   * gravar o telefone muda este bloco de coluna e o remonta, e um estado local morreria ali.
   */
  marcouAqui: boolean;
  onMarcou: () => void;
  /** Modo CONTATO: le e grava no contato do WhatsApp, so' em branco. Ignora `orcamentoId`. */
  contatoId?: number;
}

interface RespostaContato {
  origem_cliente_id: number | null;
  origem_cliente_em: string | null;
  pode_alterar: boolean;
}

export function BlocoOrigemCliente({ orcamentoId, clienteId, clienteNome, marcouAqui, onMarcou, contatoId }: Props) {
  const { usuario } = useAuth();
  const ehAdmin = usuario?.perfil === 'admin';
  const [origens, setOrigens] = useState<Origem[] | null>(null);
  const [sel, setSel] = useState<number | null>(null);
  // Quando a resposta foi dada (migration 0323) — a PRIMEIRA vez; corrigir nao a move.
  const [em, setEm] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  // Modo contato: `undefined` = ainda lendo; `null` no cliente_id = contato sem ficha.
  const [doContato, setDoContato] = useState<RespostaContato | undefined>(undefined);

  // ⚠️ Com as inativas junto: a origem ja gravada de um cliente pode ter sido desativada depois, e
  // sem ela na lista o bloco abriria sem marca num cliente que tem resposta (o mesmo cuidado do
  // combo com valor "(nao cadastrada)").
  useEffect(() => {
    api.get<Origem[]>('/api/origens-cliente', { query: { incluir_inativos: true } })
      .then(setOrigens)
      .catch(() => setOrigens([]));
  }, []);

  useEffect(() => {
    if (!contatoId) return;
    let vivo = true;
    setDoContato(undefined);
    api.get<RespostaContato>(`/api/origens-cliente/contato/${contatoId}`)
      .then((r) => {
        if (!vivo) return;
        setDoContato(r);
        setSel(r.origem_cliente_id);
        setEm(r.origem_cliente_em);
      })
      .catch(() => { if (vivo) setErro('Nao consegui ler a origem deste contato.'); });
    return () => { vivo = false; };
  }, [contatoId]);

  useEffect(() => {
    if (contatoId || !orcamentoId) return;
    let vivo = true;
    api.get<{ origem_cliente_id: number | null; origem_cliente_em: string | null; do_cliente: number | null }>(
      `/api/origens-cliente/venda/${orcamentoId}`, { query: { cliente_id: clienteId } },
    )
      .then(async (r) => {
        if (!vivo) return;
        // Cliente SEM resposta, venda COM resposta: a marca foi feita antes de ele ser identificado
        // (escolhido depois, ou criado pelo caminho 2). Ela e' desta venda, logo deste cliente — vai
        // pra ficha. ⚠️ Pela leitura do banco, nao por estado local: gravar o telefone troca o
        // bloco de coluna e o remonta, e um `useRef` morreria ali.
        if (clienteId > 1 && r.do_cliente == null && r.origem_cliente_id != null) {
          try {
            await api.put(`/api/origens-cliente/venda/${orcamentoId}`, { origem_cliente_id: r.origem_cliente_id, cliente_id: clienteId });
          } catch { /* fica so' no pedido; o proximo clique tenta de novo */ }
        }
        if (!vivo) return;
        setSel(r.origem_cliente_id);
        setEm(r.origem_cliente_em);
      })
      .catch(() => { /* sem leitura, o bloco abre sem marca — e o clique ainda grava */ });
    return () => { vivo = false; };
  }, [orcamentoId, clienteId]);

  // Ja respondida antes deste modal, e quem esta aqui nao e' admin: so' leitura.
  // Modo contato: so' em branco, pra todos — quem decide e' o servidor (`pode_alterar`), e o que
  // foi marcado NESTA tela segue trocavel enquanto ela estiver aberta.
  const travado = contatoId
    ? !doContato || (!doContato.pode_alterar && !marcouAqui)
    : !ehAdmin && sel != null && !marcouAqui;

  async function marcar(id: number) {
    if ((!orcamentoId && !contatoId) || gravando || travado) return;
    const anterior = sel;
    const proximo = sel === id ? null : id; // clicar de novo desmarca
    setSel(proximo);
    setErro(null);
    setGravando(true);
    try {
      if (contatoId) await api.put(`/api/origens-cliente/contato/${contatoId}`, { origem_cliente_id: proximo });
      else await api.put(`/api/origens-cliente/venda/${orcamentoId}`, { origem_cliente_id: proximo, cliente_id: clienteId });
      onMarcou();
      // A data e' a da primeira resposta: so' nasce quando nao havia nenhuma, e some ao desmarcar.
      setEm((a) => (proximo == null ? null : a ?? new Date().toISOString()));
    } catch (e: any) {
      setSel(anterior);
      // Falha fica NA TELA ate a proxima tentativa, nao num toast de 4s (convencao da casa).
      setErro(e?.corpo?.erro ?? e?.message ?? 'Nao deu pra gravar a origem. Tente de novo.');
    } finally {
      setGravando(false);
    }
  }

  if (!orcamentoId && !contatoId) return null;
  const visiveis = (origens ?? []).filter((o) => o.ativo || o.id === sel);

  return (
    // O mesmo painel amarelo claro do "Quem esta comprando?" logo acima (pedido dele, com print).
    <div className="mb-3 rounded-xl border border-amber-300 bg-amber-50/60 p-2.5">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[13px] font-bold text-amber-900">Como conheceu a loja?</span>
        {sel == null && <span className="text-[11px] text-gray-500">opcional</span>}
        {travado && <Lock size={12} className="text-amber-900" aria-label="Travado" />}
        {gravando && <Loader2 size={12} className="animate-spin text-gray-400" />}
      </div>
      {origens === null || (contatoId && doContato === undefined && !erro) ? (
        <div className="text-[12px] text-gray-500">carregando...</div>
      ) : visiveis.length === 0 ? (
        <div className="text-[12px] text-gray-500">Nenhuma origem cadastrada.</div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {visiveis.map((o) => {
            const marcada = sel === o.id;
            return (
              <button
                key={o.id}
                type="button"
                onClick={() => marcar(o.id)}
                disabled={gravando || travado}
                aria-pressed={marcada}
                // Cores FIXAS: o bloco tem fundo amarelo claro cravado, e par de tema aqui se moveria
                // sozinho sobre um fundo que nao se move ([DA-678]).
                // Travado, a marcada segue cheia (e' o dado) e as outras apagam — sem hover, que
                // convidaria a um clique que nao faz nada.
                className={`px-2.5 py-1 rounded-lg border text-[12px] font-semibold flex items-center gap-1 transition ${
                  marcada
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : travado
                      ? 'bg-white border-gray-300 text-gray-400 cursor-not-allowed'
                      : 'bg-white border-gray-400 text-gray-700 hover:bg-gray-100'
                }`}
              >
                {marcada && <Check size={12} />}
                {o.nome}
              </button>
            );
          })}
        </div>
      )}
      {/* A data, e — botao desabilitado DIZ por que (convencao da casa) — quem pode trocar. */}
      {sel != null && (
        <div className="text-[11px] text-gray-600 mt-1.5">
          Respondido{em ? <> em <b>{formatDataSP(em)}</b></> : ''}
          {contatoId
            ? travado
              ? ' · ja preenchida'
              : ' · da pra trocar enquanto esta tela estiver aberta'
            : travado
            ? ' · so o administrador altera'
            : ehAdmin
              ? ' · como administrador, voce pode alterar'
              : ' · da pra trocar enquanto esta tela estiver aberta'}
        </div>
      )}
      {erro && (
        <div className="mt-1.5 rounded border border-red-300 bg-red-50 px-2 py-1 text-[12px] text-red-700">{erro}</div>
      )}
    </div>
  );
}
